import React, { useState, useEffect } from 'react';
import { Popover, Tooltip, message } from 'antd';
import { MailIcon } from '@heroicons/react/outline';
import styled from 'styled-components';
import { colors, spacing } from '../../design-system/tokens';
import { outlookThreadsService } from '../../services/outlookThreadsService';

interface EmailThread {
  conversation_id: string;
  subject: string;
  message_count: number;
  last_received_at: string;
  first_received_at: string;
  participants: string[];
  is_excluded: boolean;
}

interface EmailSourcesColumnProps {
  creatorId: string;
  projectId: string | undefined;
  onThreadExclusionChange?: (creatorId: string, threadId: string, excluded: boolean) => void;
}

const EmailSourcesCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const EmailIcon = styled(MailIcon)<{ $hasThreads: boolean; $hasExcluded: boolean }>`
  width: 18px;
  height: 18px;
  color: ${props => {
    if (props.$hasExcluded) return colors.feedback.error.primary;
    if (props.$hasThreads) return colors.text.primary;
    return colors.text.tertiary;
  }};
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${props => props.$hasThreads ? colors.text.primary : colors.text.secondary};
  }
`;

const PopoverContent = styled.div`
  width: 400px;
  max-height: 500px;
  overflow-y: auto;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[3]} ${spacing[4]};
  border-bottom: 1px solid ${colors.border.secondary};
  margin-bottom: ${spacing[2]};
`;

const PopoverTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
`;

const ThreadsCount = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const ThreadsList = styled.div`
  padding: ${spacing[2]};
  max-height: 400px;
  overflow-y: auto;
`;

const ThreadItem = styled.div<{ $isExcluded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing[2]} ${spacing[3]};
  margin-bottom: ${spacing[2]};
  border-radius: 6px;
  background: ${props => props.$isExcluded ? colors.feedback.error.surface : colors.background.secondary};
  border: 1px solid ${props => props.$isExcluded ? colors.feedback.error.border : colors.border.secondary};
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.$isExcluded ? colors.feedback.error.primary : colors.border.primary};
  }
`;

const ThreadContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ThreadSubject = styled.div<{ $isExcluded: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$isExcluded ? colors.feedback.error.primary : colors.text.primary};
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ThreadMeta = styled.div`
  font-size: 11px;
  color: ${colors.text.tertiary};
`;

const ThreadToggle = styled.button<{ $isExcluded: boolean }>`
  padding: 4px 12px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid ${props => props.$isExcluded ? colors.feedback.error.border : colors.border.secondary};
  border-radius: 4px;
  background: ${props => props.$isExcluded ? colors.feedback.error.surface : 'white'};
  color: ${props => props.$isExcluded ? colors.feedback.error.primary : colors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: ${spacing[2]};
  
  &:hover {
    background: ${props => props.$isExcluded ? colors.feedback.error.primary : colors.background.tertiary};
    color: ${props => props.$isExcluded ? 'white' : colors.text.primary};
    border-color: ${props => props.$isExcluded ? colors.feedback.error.primary : colors.border.primary};
  }
`;

const EmptyState = styled.div`
  padding: ${spacing[4]};
  text-align: center;
  color: ${colors.text.tertiary};
  font-size: 12px;
`;

const LoadingState = styled.div`
  padding: ${spacing[4]};
  text-align: center;
  color: ${colors.text.secondary};
  font-size: 12px;
`;

export const EmailSourcesColumn: React.FC<EmailSourcesColumnProps> = ({
  creatorId,
  projectId,
  onThreadExclusionChange
}) => {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const loadThreads = async () => {
    if (!creatorId || !projectId) {
      setThreads([]);
      return;
    }
    
    setLoading(true);
    try {
      const response = await outlookThreadsService.getEmailThreadsForCreatorProject(
        creatorId,
        projectId,
        50,
        0
      );
      setThreads(response.threads || []);
    } catch (error) {
      console.error('Error loading email threads:', error);
      message.error('Failed to load email threads');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (popoverVisible && creatorId && projectId) {
      loadThreads();
    }
  }, [popoverVisible, creatorId, projectId]);

  const handleToggleExclusion = async (threadId: string, currentlyExcluded: boolean) => {
    if (!projectId) {
      message.error('Project ID is required');
      return;
    }
    
    try {
      const response = await outlookThreadsService.toggleThreadExclusion(
        threadId,
        projectId,
        !currentlyExcluded,
        creatorId
      );
      
      if (response.success) {
        // Update local state
        setThreads(prevThreads =>
          prevThreads.map(thread =>
            thread.conversation_id === threadId
              ? { ...thread, is_excluded: response.excluded }
              : thread
          )
        );
        
        // Notify parent component
        if (onThreadExclusionChange) {
          onThreadExclusionChange(creatorId, threadId, response.excluded);
        }
        
        message.success(
          response.excluded
            ? 'Thread excluded from analysis'
            : 'Thread included in analysis'
        );
      }
    } catch (error) {
      console.error('Error toggling thread exclusion:', error);
      message.error('Failed to update thread exclusion');
    }
  };

  const hasThreads = threads.length > 0;
  const hasExcludedThreads = threads.some(thread => thread.is_excluded);

  const getTooltipText = () => {
    if (!hasThreads) return 'No email threads';
    const excludedCount = threads.filter(t => t.is_excluded).length;
    if (excludedCount > 0) {
      return `${threads.length} thread${threads.length === 1 ? '' : 's'} (${excludedCount} excluded)`;
    }
    return `${threads.length} email thread${threads.length === 1 ? '' : 's'}`;
  };

  const popoverContent = (
    <PopoverContent>
      <PopoverHeader>
        <PopoverTitle>Email Threads</PopoverTitle>
        <ThreadsCount>
          {threads.length} thread{threads.length === 1 ? '' : 's'}
        </ThreadsCount>
      </PopoverHeader>
      
      {loading ? (
        <LoadingState>Loading email threads...</LoadingState>
      ) : threads.length === 0 ? (
        <EmptyState>No email threads found for this creator</EmptyState>
      ) : (
        <ThreadsList>
          {threads.map(thread => (
            <ThreadItem key={thread.conversation_id} $isExcluded={thread.is_excluded}>
              <ThreadContent>
                <ThreadSubject $isExcluded={thread.is_excluded}>
                  {thread.subject || 'No Subject'}
                </ThreadSubject>
                <ThreadMeta>
                  {thread.message_count} message{thread.message_count === 1 ? '' : 's'} •{' '}
                  {new Date(thread.last_received_at).toLocaleDateString()}
                </ThreadMeta>
              </ThreadContent>
              <ThreadToggle
                $isExcluded={thread.is_excluded}
                onClick={() => handleToggleExclusion(thread.conversation_id, thread.is_excluded)}
              >
                {thread.is_excluded ? 'Include' : 'Exclude'}
              </ThreadToggle>
            </ThreadItem>
          ))}
        </ThreadsList>
      )}
    </PopoverContent>
  );

  // Don't show the icon if there's no project
  if (!projectId) {
    return null;
  }

  return (
    <Popover
      content={popoverContent}
      trigger="click"
      open={popoverVisible}
      onOpenChange={setPopoverVisible}
      placement="bottomRight"
      overlayInnerStyle={{ padding: 0 }}
    >
      <Tooltip title={getTooltipText()} placement="top">
        <EmailSourcesCell>
          <EmailIcon 
            $hasThreads={hasThreads} 
            $hasExcluded={hasExcludedThreads}
          />
        </EmailSourcesCell>
      </Tooltip>
    </Popover>
  );
};

